import { connect } from 'react-redux';
import { useState, useRef, useEffect } from 'react';

import { 
  SET_UNREAD_MESSAGES_DIALOGUE_OPEN, SET_UPGRADE_TO_STANDARD_DIALOGUE_OPEN, SET_EDIT_PROJECT_MEMBERS_DIALOGUE_ID, ADD_PROJECT_MEMBER, SiteboardApiValidator,
  CREATE_STANDARD_SUBSCRIPTION_CHECKOUT, Email, SET_ADMIN_DIALOGUE_OPEN, DateStringMMDDYYYY, SET_PROJECT_CONTACTS_DIALOGUE_ID, GET_PROJECT_SCHEDULE_CONTACTS,
  SET_EDIT_PROJECT_DIALOGUE_ID,
} from '../actions/types';
import { setPath, setProfileDialogueShowing, siteboardAction, } from '../actions/defaultActions';
import { millisDurationToString, getProjectDaysRemaining, getProjectCompletionDatestring, keyCountInObject } from '../Util';

import ProjectList from './ProjectList';
import AlertCountBubble from './AlertCountBubble';
import PushButton from './PushButton';
import PerformanceMeter from './PerformanceMeter';
import UserAvatar from './UserAvatar';

// icon imports
import { CircleUserRound, } from 'lucide-react';
import DashboardIcon from '../icons/dashboardIcon';
import GlobeIcon from '../icons/globeIcon';
import GantChartIcon from '../icons/gantChartIcon';
import ExpandDownIcon from '../icons/expandDownIcon';
import Logo from '../SiteBoard_Logo';
import NotepadIcon from '../icons/notepadIcon';
import AdminIcon from '../icons/adminIcon';
import NotificationIcon from '../icons/notificationIcon';
import UpgradeIcon from '../icons/upgradeIcon';
import TeamIcon from '../icons/teamIcon';
import SettingsIcon from '../icons/settingsIcon';
import ContactIcon from '../icons/contactIcon';
import BackIcon from '../icons/backIcon';

const oneDayInMillis = 1000*60*60*24;

const Sidebar = ({ _projects, _schedules, _users, _userId, _lastViewedProjectId, _siteboardEmail, _unreadScheduleMessageThreads, _subscriptions, _subscriptionInvites, _path, setPath, setProfileDialogueShowing, siteboardAction, selectedProjectId, selectedScheduleId }) => {

  const selectedProject = selectedProjectId in _projects ? _projects[selectedProjectId] : null;
  const selectedSubscription = selectedProject !== null ? _subscriptions[selectedProject.subscriptionId] : null;

  const [projectDaysRemaining, setProjectDaysRemaining] = useState(selectedProject !== null ? getProjectDaysRemaining(selectedProject.beginDate, selectedProject.calendarDays) : '?');
  const [projectCompletionDate, setProjectCompletionDate] = useState(selectedProject !== null ? getProjectCompletionDatestring(selectedProject.beginDate, selectedProject.calendarDays) : '?');
  const updateProjectDaysRemaingTimer = useRef(null);

  //const isMyProjectsPath = _path === '' || !(projectId in _projects);
  //const isProjectDashbardPath = _path.match(/^projectId#[a-fA-F0-9-]+$/) !== null;
  const is3WeekSchedulePath = _path.match(/^projectId#([a-fA-F0-9-]+)#scheduleId#([a-fA-F0-9-]+)$/) || _path.match(/^projectId#([a-fA-F0-9-]+)#schedule$/) !== null;
  //const isDailyReportPath = _path.match(/^projectId#([a-fA-F0-9-]+)#dailyReportId#([a-fA-F0-9-]+)$/) || _path.match(/^projectId#([a-fA-F0-9-]+)#dailyReports$/) !== null;

  const getUserEmail = () => { try { return _users[_userId].email; } catch(error) { return ''; } };
  const getUserName = () => { try { return _users[_userId].firstName + ' ' + _users[_userId].lastName; } catch(error) { return ''; } };

  let totalUnreadMessages = 0;
  for(const threadId in _unreadScheduleMessageThreads) {
    if(!(_unreadScheduleMessageThreads[threadId].projectId in _projects)) continue;
    totalUnreadMessages += _unreadScheduleMessageThreads[threadId].unreadCount;
  }
  totalUnreadMessages += keyCountInObject(_subscriptionInvites);

  let isTrialSubscription = false;
  let trialTimeRemainingString = null;
  let ownedSubscription = null;
  for(const subscriptionId in _subscriptions) {
    const subscription = _subscriptions[subscriptionId];
    if(!(_userId in subscription.members)) continue;
    if(subscription.members[_userId].role !== 'Owner') continue;
    if(subscription.type !== 'Trial') continue;

    // the current user owns this trial subscription
    isTrialSubscription = true;
    ownedSubscription = subscription;
    const trialTimeRemainingMillis = subscription.endTimestamp - new Date().getTime();
    //const trialTimeRemainingMillis = oneDayInMillis*7;
    //if(trialTimeRemainingMillis < oneDayInMillis*10) { // if time until trial expiration is less than 10 days display a notice
      if(trialTimeRemainingMillis < 0) {
        trialTimeRemainingString = 'Trial expired ' + millisDurationToString(Math.abs(trialTimeRemainingMillis)) + ' ago';
      } else {
        trialTimeRemainingString = 'Trial expires in ' + millisDurationToString(trialTimeRemainingMillis);
      }
    //}
  }
  const memberCountInActiveProject = selectedProject === null ? 0 : (keyCountInObject(selectedProject.members) + keyCountInObject(selectedProject.pendingMembers));

  // Update projectDaysRemaining every second
  useEffect(() => {
    const doUpdate = () => {
      try { 
        setProjectDaysRemaining(getProjectDaysRemaining(selectedProject.beginDate, selectedProject.calendarDays)); 
      } catch(error) {
        setProjectDaysRemaining('?');
      }
      try { 
        setProjectCompletionDate(getProjectCompletionDatestring(selectedProject.beginDate, selectedProject.calendarDays)); 
      } catch(error) {
        setProjectDaysRemaining('?');
      }
    };
    clearInterval(updateProjectDaysRemaingTimer.current);
    updateProjectDaysRemaingTimer.current = setInterval(() => { doUpdate(); }, 1000);
    doUpdate();

    return () => { clearInterval(updateProjectDaysRemaingTimer.current); }; // remove interval timeout when component unloads
  }, [selectedProject, selectedProjectId, _projects]);

  return (
    <div className={'flex flex-col w-[310px] min-w-[310px] h-full max-h-screen bg-black/90 text-white overflow-y-auto'}>

      {/* Logo */}
      <div className='mt-2 pl-2 flex items-center justify-center pr-4'>
        <button  
          className={'flex text-[1.6em] font-bold items-center gap-x-2 justify-center text-white'}
          onClick={() => { setPath(''); }}
        >
          <Logo size='1em' /> {process.env.REACT_APP_WEBSITE_NAME}
        </button>
      </div>

      {/* Project Section */}
      {selectedProject !== null &&
        <div className='flex flex-col justify-center'>
          <div className='border-[#f9a928] mt-2'>
            <ProjectList projectId={selectedProject.id} />
            <div className='flex flex-col px-2'>
              {/* Project info */}
              <div className='flex flex-col mt-1 gap-0'>
                {/* Project settings */}
                <button 
                  className='group flex justify-start items-center gap-x-2 font-semibold py-2 px-2 hover:bg-neutral-700 rounded-md'
                  onClick={() => { 
                    siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: selectedProject.id });
                  }}
                >
                  <div className=''><SettingsIcon size='1.4em' /></div>
                  <div className='flex justify-center items-center gap-x-1 text-white'>Project Settings</div>
                </button>
                {/* Notice to proceed */}
                {DateStringMMDDYYYY.isValid(selectedProject.beginDate) &&
                  <div className='flex items-end gap-x-1 text-[#a59e9e] ml-2'>
                    <div className='opacity-80'>Notice to proceed:</div><div className='font-semibold'>{selectedProject.beginDate}</div>
                  </div>
                }
                {/* Days Remaining, only show project days remaining if we are able to calculate it from the project settings */}
                {(DateStringMMDDYYYY.isValid(selectedProject.beginDate) && typeof selectedProject.calendarDays === 'string' && selectedProject.calendarDays.length > 1) &&
                  <div className={`flex items-end gap-x-1 text-[#a59e9e] ml-2 ${projectDaysRemaining === '?' ? 'hidden' : ''}`}>
                    <div className='opacity-80'>Calendar days remaining:</div><div className='font-semibold'>{projectDaysRemaining}</div>
                  </div>
                }
                {/* Completion Date, only show project completion date if we are able to calculate it from the project settings */}
                {(DateStringMMDDYYYY.isValid(selectedProject.beginDate) && typeof selectedProject.calendarDays === 'string' && selectedProject.calendarDays.length > 1) &&
                  <div className={`flex items-end gap-x-1 text-[#a59e9e] ml-2 ${projectCompletionDate === '?' ? 'hidden' : ''}`}>
                    <div className='opacity-80'>Completion date:</div><div className='font-semibold'>{projectCompletionDate}</div>
                  </div>
                }
                {/*memberCountInActiveProject > 1 && 
                  <div className={`flex items-end gap-x-1 text-[#a59e9e] mt-2`}>
                    <PushButton 
                      textColor='white'
                      bgColor='bg-neutral-700'
                      hoverBgColor='bg-neutral-600'
                      onClick={() => { siteboardAction(SET_EDIT_PROJECT_MEMBERS_DIALOGUE_ID, { projectId: selectedProject.id }); }}
                    >
                      <div className='flex items-center gap-x-[17px] w-full'>
                        <TeamIcon size='1.4em' color='#f9a928' /> 
                        <div>Project Members ({keyCountInObject(selectedProject.members)})</div>
                      </div>
                    </PushButton>
                  </div>
                */}
                {/*<p className='text-[#a59e9e] font-bold'>Construction Days elapsed: {getConstructionDaysElapsed(selectedProject)}</p>*/}
                {/*(memberCountInActiveProject <= 1 && SiteboardApiValidator[ADD_PROJECT_MEMBER].isUserIdAuthorized(_userId, selectedSubscription, selectedProject)) && 
                  <div className='mt-2'>
                    <PushButton 
                      textColor='white'
                      bgColor='bg-neutral-700'
                      hoverBgColor='bg-neutral-600'
                      onClick={() => { 
                        siteboardAction(SET_EDIT_PROJECT_MEMBERS_DIALOGUE_ID, { projectId: selectedProject.id });
                      }}
                    >
                      <div className='flex items-center gap-[17px] w-full'>
                        <TeamIcon size='1.4em' color='#f9a928' />
                        <div className='flex flex-col justify-start items-start gap-0'>
                          <div className='font-bold'>Invite Your Team</div>
                          <div className='font-normal'>Click to share access</div>
                        </div>
                      </div>
                    </PushButton>
                  </div>
                */}
                {/*<div className='mt-2'>
                  <PushButton 
                    textColor='white'
                    bgColor='bg-neutral-700'
                    hoverBgColor='bg-neutral-600'
                    onMouseEnter={() => { // preload contacts
                      siteboardAction(GET_PROJECT_SCHEDULE_CONTACTS, {projectId: selectedProject.id});
                    }}
                    onClick={() => { 
                      siteboardAction(SET_PROJECT_CONTACTS_DIALOGUE_ID, { projectId: selectedProject.id });
                    }}
                  >
                    <div className='flex items-center gap-x-[20px] w-full'>
                      <div className='relative top-[-1px]'>
                        <ContactIcon size='1.4em' color='white' />
                      </div>
                      <div className=''>Distribution List ({selectedProject.contactCount})</div>
                    </div>
                  </PushButton>
                </div>*/}
              </div>
              {/* Workspace buttons */}
              <div className='flex flex-col gap-y-1'>
                {/*<div className='flex font-bold text-[#ece5e5]'>Work Spaces</div>*/}
                {/* Project dashboard button */}
                {/*<button  
                  className='flex items-center gap-x-[10px] group'
                  onClick={() => { setPath('projectId#' + selectedProject.id); }}
                >
                  <div className={isProjectDashbardPath ? 'hidden' : 'group-hover:hidden'}>
                    <DashboardIcon size='24' />
                  </div>
                  <div className={isProjectDashbardPath ? '' : 'hidden group-hover:block'}>
                    <DashboardIcon size='24' color='#1267e7' />
                  </div>
                  <p className={'' + (isProjectDashbardPath ? 'text-white' : 'text-[#a59e9e]') + ' font-semibold'}>Project Dashboard</p>
                </button>*/}
                {/* 3-week schedule button
                <button  
                  className='flex items-center gap-x-[10px] group'
                  onClick={() => { setPath('projectId#' + selectedProject.id + '#schedule'); }}
                >
                  <div className={is3WeekSchedulePath ? 'hidden' : 'group-hover:hidden'}>
                    <GantChartIcon size='24' />
                  </div>
                  <div className={is3WeekSchedulePath ? '' : 'hidden group-hover:block'}>
                    <GantChartIcon size='24' color='#471cff' />
                  </div>
                  <p className={'' + (is3WeekSchedulePath ? 'text-white' : 'text-[#a59e9e]') + ' font-semibold'}>3-Week Schedule</p>
                </button> */}
                {/* Daily Reports button */}
                {/*process.env.REACT_APP_DAILY_REPORTS_ENABLE === 'true' &&
                  <button  
                    className='flex items-center gap-x-[10px] group'
                    onClick={() => { setPath('projectId#' + selectedProject.id + '#dailyReports'); }}
                  >
                    <div className={isDailyReportPath ? 'hidden' : 'group-hover:hidden'}>
                      <NotepadIcon size='24' color='#a59e9e' />
                    </div>
                    <div className={isDailyReportPath ? '' : 'hidden group-hover:block'}>
                      <NotepadIcon size='24' color='#55fa79' />
                    </div>
                    <p className={'' + (isDailyReportPath ? 'text-white' : 'text-[#a59e9e]') + ' font-semibold'}>Daily Reports</p>
                  </button>
                */}
                {/*<div className='flex items-center gap-x-[10px]'>
                  <CrewIcon />
                  <p className='text-[#a59e9e] font-semibold'>Crew</p>
                </div>*/}
              </div>
              {is3WeekSchedulePath &&
                <div className='relative my-2 z-[0]'>
                  <PerformanceMeter selectedProjectId={selectedProject.id} selectedScheduleId={selectedScheduleId} />
                </div>
              }
            </div>
          </div>
        </div>
      }

      {/* Space filler if no project */}
      <div className='grow flex flex-col justify-center'></div>

      {/* Trial remaining time and Upgrade To Standard Button */}
      {isTrialSubscription && SiteboardApiValidator[CREATE_STANDARD_SUBSCRIPTION_CHECKOUT].isUserIdAuthorized(_userId, ownedSubscription, null) &&
        <div className='mb-2 mx-1'>
          <PushButton 
            textColor='black'
            bgColor='#f9a928'
            hoverBgColor='#f79800'
            onClick={() => { siteboardAction(SET_UPGRADE_TO_STANDARD_DIALOGUE_OPEN, { isOpen: true }); }}
          >
            <div className='flex flex-col'>
              <div className='flex items-center gap-x-[6px]'>
                <UpgradeIcon color='#000' /> 
                <div>Upgrade to Standard Plan</div>
              </div>
              {trialTimeRemainingString !== null &&
                <div className='text-red-700'>{trialTimeRemainingString}</div>
              }
            </div>
          </PushButton>
        </div>
      }

      {/* Nav Links */}
      <div className='flex flex-col'>
        {/* My projects button */}
        {selectedProject !== null &&
          <button  
            className='group flex justify-left items-center gap-x-2 font-semibold py-2 px-2 hover:bg-neutral-700 rounded-md mx-1'
            onClick={() => { setPath(''); }}
          >
            {/*<div className={isMyProjectsPath ? 'hidden' : 'group-hover:hidden'}>
              <GlobeIcon size='24' />
            </div>
            <div className={isMyProjectsPath ? '' : 'hidden group-hover:block'}>
              <GlobeIcon size='24' color='#fff168' />
            </div>
            <p className={'' + (isMyProjectsPath ? 'text-white' : 'text-[#a59e9e]') + ' font-semibold'}>Projects</p>*/}
            <div><GlobeIcon size='1.4em' color='#fff168' /></div>
            <p className={`text-white `}>All Projects</p>
          </button>
        }
        {/* Notifications button */}
        <button 
          className='group flex justify-start items-center gap-x-2 font-semibold py-2 px-2 hover:bg-neutral-700 rounded-md mx-1'
          onClick={() => {
            siteboardAction(SET_UNREAD_MESSAGES_DIALOGUE_OPEN, { isOpen: true });
          }}
        >
          <div><NotificationIcon size='1.4em' color='#fff' /></div>
          <div className='flex justify-center items-center gap-x-1 text-white'>Notifications <AlertCountBubble totalUnreadMessages={totalUnreadMessages} /></div>
        </button>
        {/* Siteboard email */}
        {(Email.isValid(_siteboardEmail)) &&
          <button
            className='group flex items-center gap-x-2 font-bold py-2 px-2 hover:bg-neutral-700 rounded-md overflow-hidden mx-1'
            onClick={() => {
              siteboardAction(SET_ADMIN_DIALOGUE_OPEN, { isOpen: true });
            }}
          >
            <div className='relative'>
              <AdminIcon color='#fff' /> 
            </div>
            <div>
              Admin Tools
            </div>
          </button>
        }
      </div>

      {/* Footer Separator */}
      <div className='w-full h-[1px] min-h-[1px] bg-white/40 my-2'></div>

      {/* My Account footer */}
      <div className='flex flex-col pb-2'>
        <button
          className='flex items-center gap-x-2 text-left hover:bg-neutral-700 rounded-lg py-1 pl-1 mx-1'
          onClick={() => { setProfileDialogueShowing(true); }}
          title='Profile'
        >
          <div>
            <UserAvatar user={_users[_userId]} showOnlineStatus={false} />
          </div>
        </button>
        
      </div>
    </div>
  );
}

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _projects: state.serverSynced.projects,
  _schedules: state.serverSynced.schedules,
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
  _lastViewedProjectId: state.localPersistent.lastViewedProjectId,
  _siteboardEmail: state.localEphemeral.siteboardEmail,
  _unreadScheduleMessageThreads: state.serverSynced.unreadScheduleMessageThreads,
  _subscriptions: state.serverSynced.subscriptions,
  _subscriptionInvites: state.serverSynced.subscriptionInvites,
  _path: state.localPersistent.path,
}};
export default connect(mapStateToProps, {setPath, setProfileDialogueShowing, siteboardAction })(Sidebar);
